import type { StatusProps } from '@cvo/ui/components/molecules/Status/Status.model';

/**
 * Composable for managing global page status messages.
 * Provides a simple way to set and display status messages across the application.
 * The last caller wins - only one status can be active at a time.
 */
export function useBanner() {
  // Use a global reactive state that persists across component instances
  const banner = useState<Banner | null>('pageBanner', () => null);
  const currentRoute = useRoute();

  /**
   * Set the global page status
   */
  function setBanner(newStatus: Banner): void {
    banner.value = {
      persistent: true,
      ...newStatus,
    };
  };

  /**
   * Clear the current page status
   */
  function clearBanner(): void {
    banner.value = null;
  };

  watch(() => currentRoute.path, () => {
    if (banner.value && !banner.value.global) {
      clearBanner();
    }
  });

  return {
    banner: readonly(banner),
    setBanner,
    clearBanner,
  };
};

export interface Banner {
  type: StatusProps['status'];
  message: StatusProps['message'];
  persistent?: StatusProps['persistent'];
  global?: boolean;
  primaryAction?: BannerAction;
  secondaryAction?: BannerAction;
}

interface BannerAction {
  label: Required<StatusProps['actionPrimary'] | StatusProps['actionSecondary']>;
  handler: () => void;
}